import { Search } from 'iconoir-react';
import { useId, useState } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/shared';
import { COMPANY_SEARCH_MINIMUM_QUERY_LENGTH } from '@savgroup-front-common/core/api/Seller/searchCompaniesQuery';
import FieldMessage from '@savgroup-front-common/core/atoms/Form/common/FieldMessage/FieldMessage';
import { FIELD_STATUS } from '@savgroup-front-common/core/atoms/Form/common/helpers/getFinalFieldState.types';
import { Label } from '@savgroup-front-common/core/atoms/Form/common/Label/Label';
import {
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '@savgroup-front-common/core/formatters';
import { PrimitiveButton } from '@savgroup-front-common/primitive/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
} from '@savgroup-front-common/primitive/ui/combobox';
import { InputGroupAddon } from '@savgroup-front-common/primitive/ui/input-group';
import { MessageType } from '@savgroup-front-common/types';
import { CompanySuggestion } from '@savgroup-front-common/types/CompanyDirectory';

import { getCompanySearchOptionKey } from './CompanySearchField.helpers';
import { useCompanySearch } from './CompanySearchField.hooks';
import { CompanySearchItem } from './CompanySearchItem';
import { MANUAL_ENTRY } from './CompanySearchItem.constants';
import { CompanySearchOption } from './CompanySearchItem.types';
import messages from './messages';

interface CompanySearchFieldProps {
  value: string;
  countryCode?: string;
  errorMessage?: string | MessageType;
  onChange: (companyName: string) => void;
  onCompanySelected: (suggestion: CompanySuggestion) => void;
  onManualEntry: () => void;
}

export function CompanySearchField({
  value,
  countryCode,
  errorMessage,
  onChange,
  onCompanySelected,
  onManualEntry,
}: CompanySearchFieldProps) {
  const inputId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const { suggestions, isSearching, hasRegisterFailed } = useCompanySearch({
    query: value,
    countryCode,
  });

  const shouldShowSuggestions =
    isOpen && value.trim().length >= COMPANY_SEARCH_MINIMUM_QUERY_LENGTH;

  return (
    <div>
      <Label
        htmlFor={inputId}
        isRequired
        status={errorMessage ? FIELD_STATUS.ERROR : undefined}
      >
        <SafeFormattedMessageWithoutSpread message={messages.label} />
      </Label>
      <Combobox<CompanySearchOption>
        items={[...suggestions, MANUAL_ENTRY]}
        filter={null}
        value={null}
        inputValue={value}
        open={shouldShowSuggestions}
        onOpenChange={setIsOpen}
        onInputValueChange={(nextValue, { reason }) => {
          if (reason === 'input-change') {
            onChange(nextValue);
          }
        }}
        onValueChange={(item) => {
          if (item === MANUAL_ENTRY) {
            onManualEntry();

            return;
          }

          if (item) {
            onCompanySelected(item);
          }
        }}
        itemToStringLabel={(item) =>
          item === MANUAL_ENTRY ? value : item.name
        }
      >
        <ComboboxInput
          id={inputId}
          showTrigger={false}
          placeholder={safeFormattedIntlString(messages.placeholder)}
          aria-invalid={!!errorMessage}
          data-testid="OnboardingForm-companyName"
        >
          <InputGroupAddon>
            <Search aria-hidden className="size-4" />
          </InputGroupAddon>
        </ComboboxInput>
        <ComboboxContent>
          {isSearching && (
            <div className="px-2 py-1.5 text-sm stext-muted-default">
              <SafeFormattedMessageWithoutSpread message={messages.searching} />
            </div>
          )}
          {!isSearching && hasRegisterFailed && (
            <div className="px-2 py-1.5 text-sm stext-muted-default">
              <SafeFormattedMessageWithoutSpread
                message={messages.registerUnavailable}
              />
            </div>
          )}
          {!isSearching && !hasRegisterFailed && suggestions.length === 0 && (
            <PrimitiveButton
              type={BUTTON_TYPES.BUTTON}
              variant="ghost"
              className="text-left font-normal whitespace-normal stext-muted-default"
              data-testid="OnboardingForm-companyNoResult"
              onClick={() => {
                setIsOpen(false);
                onManualEntry();
              }}
            >
              <SafeFormattedMessageWithoutSpread message={messages.noResult} />
            </PrimitiveButton>
          )}
          <ComboboxList>
            {(item: CompanySearchOption) => (
              <CompanySearchItem
                key={getCompanySearchOptionKey(item)}
                item={item}
              />
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      <FieldMessage message={errorMessage} status={FIELD_STATUS.ERROR} />
    </div>
  );
}
